// The audit log of a game, of the organization or of the caller (spec/dashboard.md section 12.4; spec/api-conventions.md
// sections 11 and 21.1): the filters live in the URL and go to the API under the same names, newest first, 50 a page with
// Load more; a row (`audit-<id>`, a <details>) expands to the before / after leaf diff, or "No change recorded." when both
// are null. When the log has an entry read (`GET …/audit/{entry}`), a row's id links to `?entry=<id>`: a drawer that reads
// the row on its own, so a row has a deep link.
import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { AuditEntry, Json, Page } from '../../api/types';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { DiffView } from '../../ui/DiffView';
import { Field } from '../../ui/Field';
import { CommitTextInput, Select } from '../../ui/inputs';
import { Modal, useUrlParam } from '../../ui/Modal';
import type { ZoneContext } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { DateTimeInput } from '../../ui/values';
import { useQuery } from '../../workspace/cache';
import { ACTOR_TYPES, asApiError, AUDIT_FILTER_KEYS, filtersKey, isoToSeconds, readAuditFilters, secondsToIso, type AuditFilterKey, type AuditFilters } from './keys';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import { EmptyState } from '../../ui/Banner';
import { AlertCircle, ChevronRight, ScrollText, X } from '../../ui/icons';

interface Choice {
  value: string;
  label: string;
}

export interface AuditPanelProps {
  /** the cache key prefix of this log (the filters are appended) */
  cacheKey: string;
  fetchPage: (filters: AuditFilters, cursor: string | null) => Promise<Page<AuditEntry>>;
  when: (v: string | null | undefined) => string;
  ctx?: ZoneContext;
  /** the filters offered, in this order (default: the game log's; the organization log puts `game` first) */
  filterKeys?: readonly AuditFilterKey[];
  /** the `game` filter as a select of these games (the organization log); without it `game` is an id input */
  games?: { slug: string; name: string }[];
  /** the `organization` filter as a select (the caller's own log); without it an input */
  organizations?: Choice[];
  /** the `branch` filter as a select of these branch names (the game log); without it an input for a branch id */
  branches?: string[];
  /** the `environment` filter as a select of these names (the game log: dev | stage | prod); without it an id input */
  environments?: readonly string[];
  /** reads one row (`GET …/audit/{entry}`): the row ids link to the `?entry=` drawer when given */
  fetchEntry?: (id: string) => Promise<AuditEntry>;
  /** an environment id as its slug, when known */
  envLabel?: (id: string) => string;
  /** a game id as its slug, when known (the organization log) */
  gameLabel?: (id: string) => string;
  /** a branch id as its name, when known */
  branchLabel?: (id: string) => string;
  /** an organization id as its slug, when known (the caller's own log) */
  orgLabel?: (id: string) => string;
}

const CREDENTIAL_WORD: Record<string, string> = { session: 'session', personal_token: 'personal token', service_key: 'service key' };

const TEXT_LABEL: Partial<Record<AuditFilterKey, string>> = {
  action: 'Action',
  actorId: 'Actor id',
  user: 'User',
  resourceType: 'Resource type',
  resourceId: 'Resource id',
  requestId: 'Request id',
};

interface RowLabels {
  when: (v: string | null | undefined) => string;
  envLabel?: (id: string) => string;
  gameLabel?: (id: string) => string;
  branchLabel?: (id: string) => string;
  orgLabel?: (id: string) => string;
}

export function AuditPanel({ cacheKey, fetchPage, when, ctx, filterKeys, games, organizations, branches, environments, fetchEntry, envLabel, gameLabel, branchLabel, orgLabel }: AuditPanelProps) {
  const toasts = useToasts();
  const [params, setParams] = useSearchParams();
  const keys: readonly AuditFilterKey[] = filterKeys ?? (games ? ['game', ...AUDIT_FILTER_KEYS] : AUDIT_FILTER_KEYS);
  const filters = readAuditFilters(params, keys);
  const listKey = `${cacheKey}?${filtersKey(filters)}`;
  const listQ = useQuery(listKey, () => fetchPage(filters, null));
  const first = listQ.data;
  const labels: RowLabels = { when, envLabel, gameLabel, branchLabel, orgLabel };

  const [more, setMore] = useState<{ base: Page<AuditEntry>; items: AuditEntry[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const firstRef = useRef(first);
  firstRef.current = first;
  const extra = more && more.base === first ? more : null;
  const rows = [...(first?.items ?? []), ...(extra?.items ?? [])];
  const nextCursor = extra ? extra.cursor : (first?.nextCursor ?? null);

  const loadMore = async () => {
    if (!first || !nextCursor || loadingMore) return;
    const base = first;
    setLoadingMore(true);
    try {
      const page = await fetchPage(filters, nextCursor);
      if (firstRef.current !== base) return;
      setMore({ base, items: [...(extra?.items ?? []), ...(page.items ?? [])], cursor: page.nextCursor ?? null });
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setLoadingMore(false);
    }
  };

  const setFilter = (key: AuditFilterKey, value: string | null) => {
    const next = new URLSearchParams(params);
    const v = value?.trim() ?? '';
    if (v) next.set(key, v);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const clear = () => {
    const next = new URLSearchParams(params);
    for (const k of keys) next.delete(k);
    setParams(next, { replace: true });
  };
  const anyFilter = Object.keys(filters).length > 0;

  const text = (key: AuditFilterKey, label: string, placeholder?: string) => (
    <Field key={key} label={label}>
      <CommitTextInput size="s" aria-label={label} placeholder={placeholder} value={filters[key] ?? ''} onCommit={(v) => setFilter(key, v)} />
    </Field>
  );
  const select = (key: AuditFilterKey, label: string, choices: readonly Choice[]) => (
    <Field key={key} label={label}>
      <Select size="s" aria-label={label} value={filters[key] ?? ''} onChange={(e) => setFilter(key, e.target.value || null)}>
        <option value="">any</option>
        {choices.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </Select>
    </Field>
  );
  const control = (key: AuditFilterKey) => {
    switch (key) {
      case 'actorType':
        return select(key, 'Actor type', ACTOR_TYPES.map((t) => ({ value: t, label: t })));
      case 'game':
        return games ? select(key, 'Game', games.map((g) => ({ value: g.slug, label: g.name }))) : text(key, 'Game id');
      case 'organization':
        return organizations ? select(key, 'Organization', organizations) : text(key, 'Organization');
      case 'branch':
        return branches ? select(key, 'Branch', branches.map((b) => ({ value: b, label: b }))) : text(key, 'Branch id');
      case 'environment':
        return environments ? select(key, 'Environment', environments.map((e) => ({ value: e, label: e }))) : text(key, 'Environment id');
      case 'from':
      case 'to': {
        const label = key === 'from' ? 'From' : 'To';
        return (
          <Field key={key} label={label}>
            <DateTimeInput aria-label={label} allowEmpty ctx={ctx} value={isoToSeconds(filters[key])} onCommit={(s) => setFilter(key, secondsToIso(s))} />
          </Field>
        );
      }
      case 'user':
        return text(key, 'User', 'id or email');
      default:
        return text(key, TEXT_LABEL[key] ?? key);
    }
  };
  const error = listQ.error ? asApiError(listQ.error) : null;

  const entryLink = fetchEntry
    ? (id: string) => {
        const next = new URLSearchParams(params);
        next.set('entry', id);
        return `?${next.toString()}`;
      }
    : undefined;

  return (
    <div className="settings-block audit-panel">
      <form className="page-toolbar audit-toolbar" role="search" aria-label="Audit filters" onSubmit={(e) => e.preventDefault()}>
        <div className="form-grid settings-filters">
          {keys.map(control)}
          <div className="settings-filters-clear">
            <Button size="s" variant="ghost" icon={<X aria-hidden="true" />} onClick={clear} disabled={!anyFilter}>
              Clear
            </Button>
          </div>
        </div>
      </form>
      {error && (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </div>
          <Button size="s" onClick={listQ.refetch}>
            Retry
          </Button>
        </div>
      )}
      <div className="pane">
        <div className="pane-body flush">
          {listQ.loading && !first ? (
            <SkeletonRows rows={4} />
          ) : rows.length === 0 && !error ? (
            <EmptyState icon={<ScrollText />} title={anyFilter ? 'No audit row matches these filters.' : 'No audit rows yet.'} body={anyFilter ? 'Widen or clear the filters to see more of the log.' : 'Every write is recorded here with who made it, when and what changed.'} />
          ) : (
            <ol className="audit-list">
              {rows.map((e) => (
                <li key={e.id}>
                  <AuditRow entry={e} labels={labels} link={entryLink?.(e.id)} />
                </li>
              ))}
            </ol>
          )}
        </div>
        {nextCursor && (
          <div className="pane-foot settings-more">
            <span className="muted small">{rows.length} loaded, more available</span>
            <div className="spacer" />
            <Button size="s" data-testid="audit-load-more" onClick={() => void loadMore()} busy={loadingMore}>
              Load more
            </Button>
          </div>
        )}
      </div>
      {fetchEntry && <EntryDrawer cacheKey={cacheKey} fetchEntry={fetchEntry} labels={labels} />}
    </div>
  );
}

function RowFacts({ entry: e, labels }: { entry: AuditEntry; labels: RowLabels }) {
  const { when, envLabel, gameLabel, branchLabel, orgLabel } = labels;
  return (
    <>
      <span className="audit-time">{when(e.occurredAt)}</span>
      <span className="audit-actor" title={e.actor.id}>
        {e.actor.label} {e.actor.credential ? <Badge tone="info">{CREDENTIAL_WORD[e.actor.credential] ?? e.actor.credential}</Badge> : <Badge>{e.actor.type}</Badge>}
      </span>
      <code className="audit-action">{e.action}</code>
      <span className="audit-resource">
        {e.resourceType} <code className="small">{e.resourceId}</code>
      </span>
      {orgLabel && e.organizationId && <span className="muted small">organization {orgLabel(e.organizationId)}</span>}
      {gameLabel && e.gameId && <span className="muted small">game {gameLabel(e.gameId)}</span>}
      {e.branchId && (
        <span className="muted small">
          branch <code>{branchLabel ? branchLabel(e.branchId) : e.branchId}</code>
        </span>
      )}
      {e.environmentId && <span className="muted small">environment {envLabel ? envLabel(e.environmentId) : <code>{e.environmentId}</code>}</span>}
      <span className="muted small audit-request">
        request <code>{e.requestId}</code>
      </span>
    </>
  );
}

function ChangeBody({ entry: e }: { entry: AuditEntry }) {
  const nothing = (e.before === null || e.before === undefined) && (e.after === null || e.after === undefined);
  return nothing ? <p className="muted">No change recorded.</p> : <DiffView before={(e.before ?? undefined) as Json | undefined} after={(e.after ?? undefined) as Json | undefined} />;
}

function AuditRow({ entry: e, labels, link }: { entry: AuditEntry; labels: RowLabels; link?: string }) {
  return (
    <details className="audit-row" data-testid={`audit-${e.id}`}>
      <summary>
        <ChevronRight className="audit-chevron" aria-hidden="true" />
        {link && (
          <Link to={link} className="small" title={`Open entry ${e.id}`} aria-label={`Entry ${e.id}`}>
            <code>{e.id.slice(0, 8)}</code>
          </Link>
        )}
        <RowFacts entry={e} labels={labels} />
      </summary>
      <div className="audit-body">
        <ChangeBody entry={e} />
      </div>
    </details>
  );
}

/** `?entry=<id>`: one row read on its own (`GET …/audit/{entry}`), shown in a drawer; a `404` says the row is not here. */
function EntryDrawer({ cacheKey, fetchEntry, labels }: { cacheKey: string; fetchEntry: (id: string) => Promise<AuditEntry>; labels: RowLabels }) {
  const entry = useUrlParam('entry');
  const id = entry.value;
  const q = useQuery(id ? `${cacheKey}:entry:${id}` : null, () => fetchEntry(id!));
  if (!id) return null;
  const error = q.error ? asApiError(q.error) : null;
  const e = q.data && q.data.id === id ? q.data : undefined;
  return (
    <Modal drawer open size="l" title={e ? `Audit entry ${e.action}` : 'Audit entry'} onClose={entry.close}>
      <div data-testid="entry-drawer">
        {e ? (
          <>
            <dl className="kv">
              <dt>Id</dt>
              <dd>
                <code>{e.id}</code>
              </dd>
            </dl>
            <p className="audit-entry-facts">
              <RowFacts entry={e} labels={labels} />
            </p>
            <ChangeBody entry={e} />
          </>
        ) : error ? (
          <LoadError>{error.status === 404 ? 'No audit row with this id in this log.' : error.message}</LoadError>
        ) : (
          <SkeletonRows rows={3} label="Loading the entry" />
        )}
      </div>
    </Modal>
  );
}
